import type {
  Course,
  CourseCategory,
  CourseDetail,
  CourseReview,
  Lesson,
} from '../types'

/**
 * Fallback courses data. Mirrors the live API shape so the gallery
 * + sales pages stay alive in dev. Uses real images from /public.
 */

const baseCourse = (overrides: Partial<Course>): Course => ({
  id: '',
  title: '',
  slug: '',
  description: null,
  thumbnail: null,
  price: 0,
  oldPrice: null,
  instructorId: 'instructor-abdallah',
  categoryId: null,
  level: 'beginner',
  duration: null,
  isPublished: true,
  isFeatured: false,
  sortOrder: 0,
  createdAt: '',
  updatedAt: '',
  instructorName: 'عبدالله عيسى',
  instructorAvatar: null,
  studentsCount: 0,
  lessonsCount: 0,
  avgRating: null,
  reviewsCount: 0,
  ...overrides,
})

export const COURSE_CATEGORIES_FALLBACK: CourseCategory[] = [
  {
    id: 'cat-ai',
    name: 'الذكاء الاصطناعي',
    slug: 'ai',
    description: null,
    icon: 'brain',
    sortOrder: 1,
    createdAt: '',
  },
  {
    id: 'cat-design',
    name: 'التصميم',
    slug: 'design',
    description: null,
    icon: 'palette',
    sortOrder: 2,
    createdAt: '',
  },
  {
    id: 'cat-video',
    name: 'الفيديو',
    slug: 'video',
    description: null,
    icon: 'video',
    sortOrder: 3,
    createdAt: '',
  },
  {
    id: 'cat-web',
    name: 'تطوير الويب',
    slug: 'web',
    description: null,
    icon: 'code',
    sortOrder: 4,
    createdAt: '',
  },
]

export const COURSES_FALLBACK: Course[] = [
  baseCourse({
    id: 'course-ai-creators',
    title: 'الذكاء الاصطناعي للمبدعين',
    slug: 'ai-for-creators',
    description:
      'كورس متكامل يأخذك من الصفر للاحتراف في توليد الصور والفيديو والمحتوى الإبداعي بالـ AI. مشاريع حقيقية، أدوات احترافية، ومجتمع داعم.',
    thumbnail: '/bg1.webp',
    price: 1499,
    oldPrice: 2499,
    categoryId: 'cat-ai',
    categoryName: 'الذكاء الاصطناعي',
    categorySlug: 'ai',
    level: 'intermediate',
    duration: 1440, // 24 hours in minutes
    isFeatured: true,
    sortOrder: 1,
    studentsCount: 1230,
    lessonsCount: 42,
    avgRating: 4.9,
    reviewsCount: 156,
  }),
  baseCourse({
    id: 'course-image-mastery',
    title: 'احتراف توليد الصور بـ Midjourney و Flux',
    slug: 'image-generation-mastery',
    description:
      'كل ما تحتاجه لتحترف Midjourney و Flux: prompts متقدمة، أساليب فنية، تحكم كامل في النتيجة.',
    thumbnail: '/bg2.webp',
    price: 999,
    oldPrice: 1499,
    categoryId: 'cat-ai',
    categoryName: 'الذكاء الاصطناعي',
    categorySlug: 'ai',
    level: 'intermediate',
    duration: 1080,
    isFeatured: true,
    sortOrder: 2,
    studentsCount: 840,
    lessonsCount: 28,
    avgRating: 4.8,
    reviewsCount: 92,
  }),
  baseCourse({
    id: 'course-ai-video',
    title: 'فيديو احترافي بالذكاء الاصطناعي',
    slug: 'ai-video-pro',
    description:
      'من الفكرة للستوري بورد للفيديو النهائي. Sora و Veo و Runway و Kling — متى تستخدم إيه ولماذا.',
    thumbnail: '/bg3.webp',
    price: 1199,
    oldPrice: null,
    categoryId: 'cat-video',
    categoryName: 'الفيديو',
    categorySlug: 'video',
    level: 'advanced',
    duration: 960,
    isFeatured: true,
    sortOrder: 3,
    studentsCount: 510,
    lessonsCount: 24,
    avgRating: 4.9,
    reviewsCount: 64,
  }),
  baseCourse({
    id: 'course-design-foundation',
    title: 'أساسيات التصميم الجرافيكي',
    slug: 'design-foundation',
    description:
      'كورس شامل في أساسيات التصميم: نظرية الألوان، التايبوغرافي، الكومبوزشن، والـ branding.',
    thumbnail: '/bg4.webp',
    price: 799,
    oldPrice: null,
    categoryId: 'cat-design',
    categoryName: 'التصميم',
    categorySlug: 'design',
    level: 'beginner',
    duration: 720,
    sortOrder: 4,
    studentsCount: 380,
    lessonsCount: 18,
    avgRating: 4.7,
    reviewsCount: 41,
  }),
  baseCourse({
    id: 'course-react-cloudflare',
    title: 'بناء مواقع كاملة بـ React و Cloudflare',
    slug: 'react-cloudflare',
    description:
      'تطوير ونشر موقع كامل على Cloudflare Workers + Pages. React, Vite, Tailwind, D1, R2.',
    thumbnail: '/bg5.webp',
    price: 1399,
    oldPrice: 1999,
    categoryId: 'cat-web',
    categoryName: 'تطوير الويب',
    categorySlug: 'web',
    level: 'advanced',
    duration: 1320,
    sortOrder: 5,
    studentsCount: 220,
    lessonsCount: 36,
    avgRating: 4.9,
    reviewsCount: 28,
  }),
  baseCourse({
    id: 'course-ui-ux',
    title: 'تصميم UI/UX من الصفر للاحتراف',
    slug: 'ui-ux-mastery',
    description:
      'منهج عملي لتصميم واجهات احترافية: research، wireframes، design systems، testing.',
    thumbnail: '/bg6.webp',
    price: 999,
    oldPrice: null,
    categoryId: 'cat-design',
    categoryName: 'التصميم',
    categorySlug: 'design',
    level: 'intermediate',
    duration: 840,
    sortOrder: 6,
    studentsCount: 290,
    lessonsCount: 22,
    avgRating: 4.8,
    reviewsCount: 35,
  }),
]

const lesson = (overrides: Partial<Lesson>): Lesson => ({
  id: '',
  courseId: '',
  title: '',
  slug: '',
  description: null,
  videoUrl: null,
  duration: null,
  sortOrder: 0,
  isPreview: false,
  createdAt: '',
  updatedAt: '',
  ...overrides,
})

const lessonsByCourse: Record<string, Lesson[]> = {
  'course-ai-creators': [
    lesson({
      id: 'lesson-ai-1',
      courseId: 'course-ai-creators',
      title: 'مقدمة شاملة عن الذكاء الاصطناعي للمبدعين',
      slug: 'intro',
      duration: 18 * 60,
      isPreview: true,
      sortOrder: 1,
    }),
    lesson({
      id: 'lesson-ai-2',
      courseId: 'course-ai-creators',
      title: 'إعداد بيئة العمل والأدوات',
      slug: 'setup',
      duration: 25 * 60,
      sortOrder: 2,
    }),
    lesson({
      id: 'lesson-ai-3',
      courseId: 'course-ai-creators',
      title: 'أساسيات Prompt Engineering',
      slug: 'prompts-101',
      duration: 32 * 60,
      isPreview: true,
      sortOrder: 3,
    }),
    lesson({
      id: 'lesson-ai-4',
      courseId: 'course-ai-creators',
      title: 'توليد الصور: Midjourney من الأساس',
      slug: 'midjourney',
      duration: 45 * 60,
      sortOrder: 4,
    }),
    lesson({
      id: 'lesson-ai-5',
      courseId: 'course-ai-creators',
      title: 'تحكم متقدم في Midjourney + Style Refs',
      slug: 'midjourney-pro',
      duration: 50 * 60,
      sortOrder: 5,
    }),
    lesson({
      id: 'lesson-ai-6',
      courseId: 'course-ai-creators',
      title: 'Flux: البديل المفتوح',
      slug: 'flux',
      duration: 38 * 60,
      sortOrder: 6,
    }),
    lesson({
      id: 'lesson-ai-7',
      courseId: 'course-ai-creators',
      title: 'تحرير الصور بـ Nano Banana',
      slug: 'nano-banana',
      duration: 28 * 60,
      sortOrder: 7,
    }),
    lesson({
      id: 'lesson-ai-8',
      courseId: 'course-ai-creators',
      title: 'مشروع 1: حملة بصرية متكاملة',
      slug: 'project-1',
      duration: 60 * 60,
      sortOrder: 8,
    }),
  ],
  'course-image-mastery': [
    lesson({
      id: 'lesson-im-1',
      courseId: 'course-image-mastery',
      title: 'مقدمة عن مولّدات الصور',
      slug: 'intro',
      duration: 15 * 60,
      isPreview: true,
      sortOrder: 1,
    }),
    lesson({
      id: 'lesson-im-2',
      courseId: 'course-image-mastery',
      title: 'بنية الـ Prompt الفعّال',
      slug: 'prompt-anatomy',
      duration: 28 * 60,
      sortOrder: 2,
    }),
    lesson({
      id: 'lesson-im-3',
      courseId: 'course-image-mastery',
      title: 'Style References و Image Prompts',
      slug: 'style-refs',
      duration: 35 * 60,
      sortOrder: 3,
    }),
  ],
}

const reviewsByCourse: Record<string, CourseReview[]> = {
  'course-ai-creators': [
    {
      id: 'rev-1',
      courseId: 'course-ai-creators',
      userId: 'u1',
      rating: 5,
      comment:
        'كورس غيّرلي شغلي. اتعلمت أوظف الـ AI في كل خطوة من اليوم وكسبت وقت كتير.',
      isApproved: true,
      userName: 'Ahmed Hassan',
      userAvatar: null,
      createdAt: '2025-02-10T00:00:00Z',
    },
    {
      id: 'rev-2',
      courseId: 'course-ai-creators',
      userId: 'u2',
      rating: 5,
      comment:
        'أحسن مدرس عربي للذكاء الاصطناعي. الشرح بسيط، المشاريع عملية، والمتابعة بعد الكورس مش وحشة.',
      isApproved: true,
      userName: 'Sarah Mahmoud',
      userAvatar: null,
      createdAt: '2025-02-12T00:00:00Z',
    },
    {
      id: 'rev-3',
      courseId: 'course-ai-creators',
      userId: 'u3',
      rating: 5,
      comment: 'بعد ما خلصت الكورس اشتغلت أول مشروع AI خلال أسبوع.',
      isApproved: true,
      userName: 'Omar Khaled',
      userAvatar: null,
      createdAt: '2025-02-18T00:00:00Z',
    },
  ],
  'course-image-mastery': [
    {
      id: 'rev-4',
      courseId: 'course-image-mastery',
      userId: 'u4',
      rating: 5,
      comment: 'أحسن دليل عربي لـ Midjourney. الـ prompts اللي قاعدته بنبني عليها يومياً.',
      isApproved: true,
      userName: 'Mona Adel',
      userAvatar: null,
      createdAt: '2025-02-20T00:00:00Z',
    },
  ],
}

export function getFallbackCourse(slug: string): CourseDetail | null {
  const c = COURSES_FALLBACK.find((c) => c.slug === slug)
  if (!c) return null
  return {
    ...c,
    lessons: lessonsByCourse[c.id] ?? [],
    reviews: reviewsByCourse[c.id] ?? [],
  }
}
